/**
 * Normalizes raw job data into a consistent UI object.
 */
export const normalizeJob = (job) => {
  if (!job) return null;

  // 1. Basic Info
  const id = job.job_id || job.id || Math.random().toString(36).substr(2, 9);
  const title = job.job_title || 'Untitled Position';
  const company = job.employer_name || 'Unknown Company';
  const logo = job.employer_logo || null;
  
  // 2. Location logic
  const city = job.job_city || '';
  const state = job.job_state || '';
  const isRemote = job.job_is_remote === true || String(job.job_is_remote).toLowerCase() === 'true';

  // 3. Salary Label (for UI) and Raw Value (for Filter)
  let salaryLabel = 'Competitive';
  const minSal = Number(job.job_min_salary) || 0;
  if (minSal > 0 && job.job_max_salary) {
    salaryLabel = `₹${(minSal / 1000).toFixed(0)}k - ₹${(job.job_max_salary / 1000).toFixed(0)}k`;
  }

  return {
    id,
    title,
    company,
    logo,
    location: {
      city,
      state,
      full: isRemote ? 'Remote' : (city && state ? `${city}, ${state}` : city || 'In-office')
    },
    salaryLabel,
    // Keep raw fields for filtering
    job_is_remote: isRemote,
    job_employment_types: Array.isArray(job.job_employment_types) ? job.job_employment_types : [],
    job_min_salary: minSal,
    description: job.job_description || '',
    postedAt: job.job_posted_at_datetime_utc || job.job_posted_at || new Date().toISOString(),
    timestamp: job.job_posted_at_timestamp || 0,
    applyUrl: job.job_apply_link || ''
  };
};

export const normalizeJobsData = (rawData) => {
  if (!rawData) return {};
  const normalized = {};
  Object.keys(rawData).forEach(city => {
    normalized[city] = Array.isArray(rawData[city]) 
      ? rawData[city].map(job => normalizeJob(job)) 
      : [];
  });
  return normalized;
};

/**
 * ADDED EXPORT: Helper to format relative time (e.g., "16 hours ago")
 */
export const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  
  // 1. Calculate the difference
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  // 2. Error Handling & "Future" Dates (Timezone issues)
  if (isNaN(diffInSeconds)) return 'Recently';
  
  // If the diff is negative or less than 1 min, it's brand new
  if (diffInSeconds < 60) return 'Just now';
  
  // 3. Standard Logic
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  
  // 4. Multi-day Logic
  const days = Math.floor(diffInSeconds / 86400);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
};